/**
 * "The food is up" — the one thing this terminal could not say.
 *
 * A waiter fired a course, the kitchen bumped it, and nothing on the till
 * changed. The pass had to shout, or somebody had to walk over and look.
 * Whether a tab's food is ready is a fact the kitchen owns, so it is read from
 * `kds` per tab and course rather than carried on the order — the order ledger
 * knowing what a docket is would be the wrong dependency in the wrong
 * direction, and it is what keeps a second screen or a printer a change to the
 * kitchen alone.
 *
 * **Ready means every station has bumped it**, not the first one. A main routed
 * to the grill and the larder is not up because the grill is done, and sending
 * somebody to the pass for half a table's food is worse than telling them
 * nothing at all.
 *
 * Drawn in the three places a waiter looks: the tile in the room and the row
 * in the tab list (`food-up.tsx`), and each course heading on the tab itself
 * (`tab-lines.ts`).
 */

import { displayLocale, useReadiness, type CourseReadiness } from '@restaurant/api-client';

export interface FoodUp {
  /** Courses every station has finished. */
  ready: CourseReadiness[];
  /** Courses still with a station, and how far along they are. */
  waiting: CourseReadiness[];
}

const NOTHING: FoodUp = { ready: [], waiting: [] };

/**
 * Readiness for the tabs on screen, ready to ask about one at a time.
 *
 * One request for every tab in the view rather than one per tile: a room can
 * hold thirty tables, and thirty polling queries is a request every half second
 * from a tablet that is also taking orders.
 */
export function useFoodUp(orderIds: (string | null)[]): (orderId: string | null) => FoodUp {
  const ids = orderIds.filter((id): id is string => id !== null);
  const readiness = useReadiness(ids);

  const byOrder = new Map<string, FoodUp>();
  for (const row of readiness.data ?? []) {
    const found = byOrder.get(row.order_id) ?? { ready: [], waiting: [] };
    // A course nothing was routed for has no docket and no answer to give;
    // reading it as "waiting" would leave a tab permanently half-cooked.
    if (row.ready) found.ready.push(row);
    else if (row.stations > 0) found.waiting.push(row);
    byOrder.set(row.order_id, found);
  }

  return (orderId) => (orderId === null ? NOTHING : (byOrder.get(orderId) ?? NOTHING));
}

/** What to say about it in words, for a screen reader and an aria-label. */
export function describeFoodUp(food: FoodUp): string {
  if (food.ready.length === 0) return '';
  if (food.ready.length === 1) return `course ${food.ready[0]?.course_no} is up`;
  return `${food.ready.length} courses are up`;
}

/** A time of day as the till shows it: when a course came up. */
export function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(displayLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  });
}
